import {
  type CSSProperties,
  type FunctionComponent,
  type MouseEvent,
  useLayoutEffect,
  useRef,
} from "react";
import type { Ad, BmgBannerProps } from "@/shared/ui/ad/bmgAdModel";
import {
  type BmgBannerPresentation,
  useBmgBannerPresenter,
} from "@/shared/ui/ad/useBmgBannerPresenter";
import { ComponentScope } from "@/shared/ui/component-hierarchy/ComponentScope";
import {
  type ToggleEvent,
  useTogglePresenter,
} from "@/shared/ui/component-hierarchy/useTogglePresenter";

export type { AdCategory } from "@/shared/ui/ad/bmgAdModel";

export const BmgBanner: FunctionComponent<BmgBannerProps> = (
  props: BmgBannerProps,
) => {
  const presentation = useBmgBannerPresenter(props);
  return (
    <ComponentScope node={presentation.node}>
      <BmgBannerView {...presentation} />
    </ComponentScope>
  );
};

export const BmgBannerView: FunctionComponent<
  Omit<BmgBannerPresentation, "node">
> = ({
  displayedAd,
  imageUrl,
  clickUrl,
  frameClassName,
  frameStyle,
  presentationKey,
  shouldRender,
  priority,
  concealR18,
}: Omit<BmgBannerPresentation, "node">) => {
  if (!shouldRender) return null;
  return (
    <aside
      aria-label={imageUrl ? "広告" : undefined}
      aria-hidden={displayedAd !== undefined && !imageUrl ? true : undefined}
      aria-busy={displayedAd === undefined ? true : undefined}
      className="flex justify-center border-b border-border bg-background px-2 py-2"
    >
      <AdFrame
        key={presentationKey}
        ad={displayedAd}
        clickUrl={clickUrl}
        frameClassName={frameClassName}
        frameStyle={frameStyle}
        imageUrl={imageUrl}
        priority={priority}
        concealR18={concealR18}
      />
    </aside>
  );
};

interface AdFrameProps {
  ad: Ad | null | undefined;
  clickUrl: string | null;
  frameClassName: string;
  frameStyle: CSSProperties;
  imageUrl: string | null;
  priority: boolean;
  concealR18: boolean;
}

const AdFrame: FunctionComponent<AdFrameProps> = (props: AdFrameProps) => {
  const concealEligible =
    props.concealR18 && props.ad?.category === "r18" && props.imageUrl !== null;
  const presentation = useTogglePresenter(
    false,
    (revealed) =>
      props.concealR18 &&
      props.ad?.category === "r18" &&
      props.imageUrl !== null &&
      !revealed,
  );
  return (
    <ComponentScope node={presentation.node}>
      <AdFrameView
        {...props}
        revealed={presentation.pressed}
        isConcealed={concealEligible && !presentation.pressed}
        onEvent={presentation.onEvent}
      />
    </ComponentScope>
  );
};
interface AdFrameViewProps extends AdFrameProps {
  revealed: boolean;
  isConcealed: boolean;
  onEvent: (event: ToggleEvent) => void;
}
const AdFrameView: FunctionComponent<AdFrameViewProps> = ({
  ad,
  clickUrl,
  frameClassName,
  frameStyle,
  imageUrl,
  priority,
  revealed,
  isConcealed,
  onEvent,
}: AdFrameViewProps) => {
  const linkRef = useRef<HTMLAnchorElement>(null);
  const figureRef = useRef<HTMLElement>(null);
  const transferFocus = useRef(false);
  useLayoutEffect(() => {
    if (revealed && transferFocus.current) {
      (linkRef.current ?? figureRef.current)?.focus({ preventScroll: true });
      transferFocus.current = false;
    }
  }, [revealed]);
  const imageAlt = ad?.image_alt?.trim() || ad?.title.trim() || "広告";

  const image = imageUrl && (
    <img
      src={imageUrl}
      alt={isConcealed ? "" : imageAlt}
      aria-hidden={isConcealed ? true : undefined}
      loading={priority ? "eager" : "lazy"}
      fetchPriority={priority ? "high" : undefined}
      // 配信元(storage.nijiurachan.net)はリファラ(ホットリンク)保護があり、
      // 許可ドメイン以外（localhost 等）からの Referer を 403 で弾く。
      // Referer を送らなければ環境を問わず取得でき、閲覧中スレッドのURLを
      // 広告CDNへ漏らさないプライバシー面のメリットもある。
      referrerPolicy="no-referrer"
      className={`h-full w-full object-contain${isConcealed ? " r18-spoiler-image" : ""}`}
    />
  );

  if (!imageUrl) {
    return <div className={frameClassName} style={frameStyle} />;
  }

  if (isConcealed) {
    return (
      <button
        type="button"
        aria-label="R18広告を表示"
        onClick={(event: MouseEvent<HTMLButtonElement>): void => {
          transferFocus.current =
            document.activeElement === event.currentTarget;
          onEvent({ type: "view-toggle.requested", value: true });
        }}
        className={`${frameClassName} relative cursor-pointer border-0 bg-transparent p-0 text-inherit focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary`}
        style={frameStyle}
      >
        {image}
        <span
          aria-hidden="true"
          className="absolute inset-0 z-10 flex items-center justify-center bg-background/80 px-2 text-center font-medium text-foreground"
        >
          R18広告を表示
        </span>
      </button>
    );
  }

  return clickUrl ? (
    <a
      ref={linkRef}
      href={clickUrl}
      target="_blank"
      rel="noopener noreferrer sponsored"
      className={frameClassName}
      style={frameStyle}
    >
      {image}
    </a>
  ) : (
    <figure
      ref={figureRef}
      tabIndex={revealed ? -1 : undefined}
      className={frameClassName}
      style={frameStyle}
    >
      {image}
    </figure>
  );
};
